import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const UnplacedTray = ({ locations, outsideLocations = [], noFixedSpot = [], open: controlledOpen = false, onOpenChange, onSelect }) => {
  const [open, setOpen] = useState(controlledOpen);
  const isOpen = onOpenChange ? controlledOpen : open;
  const toggle = () => {
    const next = !isOpen;
    if (onOpenChange) onOpenChange(next);
    else setOpen(next);
  };
  const groups = useMemo(() => locations.reduce((result, location) => {
    const key = location.area || location.location_code.split('-')[0] || 'Other';
    result[key] = [...(result[key] || []), location];
    return result;
  }, {}), [locations]);
  const outsideGroups = useMemo(() => outsideLocations.reduce((result, location) => {
    if (location.placement?.structure !== 'shelving') return result;
    const section = location.placement.section;
    result[section] = [...(result[section] || []), location];
    return result;
  }, {}), [outsideLocations]);
  const genericOutside = outsideLocations.filter((location) => location.placement?.structure !== 'shelving');

  return (
    <aside onWheel={(event) => event.stopPropagation()} className="absolute right-3 top-20 z-30 flex max-h-[70%] w-64 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
      <button type="button" onClick={toggle} className="flex w-full items-center justify-between border-b border-gray-200 px-3 py-2 text-left">
        <span className="text-xs font-semibold text-gray-800">Unplaced locations <span className="text-gray-400">({locations.length})</span></span>
        {isOpen ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-2">
          {Object.entries(groups).map(([group, rows]) => (
            <section key={group} className="mb-2 last:mb-0">
              <p className="px-1 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{group} · {rows.length}</p>
              {rows.map((location) => (
                <button
                  type="button"
                  key={location.location_code}
                  onClick={() => onSelect(location)}
                  className="block w-full truncate rounded px-1.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-50"
                >
                  {location.location_code}
                </button>
              ))}
            </section>
          ))}
          {outsideLocations.length > 0 && (
            <section className="mb-2 last:mb-0">
              <p className="px-1 py-1 text-[10px] font-semibold uppercase tracking-widest text-red-500">Outside approved structure · {outsideLocations.length}</p>
              {Object.entries(outsideGroups).map(([section, rows]) => <div key={section} className="mb-2">
                <p className="px-1 py-1 text-[11px] text-gray-600">B-{String(section).padStart(2, '0')} is shelving section {section}; the approved frame holds sections 01-08 on the B face and 09-13 on the SM face. Needs Bryan&apos;s physical mapping.</p>
                {rows.map((location) => (
                  <button type="button" key={location.location_code} onClick={() => onSelect(location)} className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-50">
                    <span className="min-w-0 flex-1 truncate">{location.location_code}<span className="ml-1 text-[10px] text-gray-400">B{String(location.placement?.bay || '?').padStart(2, '0')} · L{location.placement?.level || '?'}</span></span>
                    <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] ${location.availability_state === 'Available' ? 'bg-emerald-50 text-emerald-700' : location.availability_state === 'Blocked' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{location.availability_state || 'Unknown'}</span>
                  </button>
                ))}
              </div>)}
              {genericOutside.map((location) => (
                <button type="button" key={location.location_code} onClick={() => onSelect(location)} className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-50">
                  <span className="min-w-0 flex-1 truncate">{location.location_code}<span className="ml-1 text-[10px] text-gray-400">B{String(location.placement?.bay || '?').padStart(2, '0')} · L{location.placement?.level || '?'}</span></span>
                  <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] ${location.availability_state === 'Available' ? 'bg-emerald-50 text-emerald-700' : location.availability_state === 'Blocked' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{location.availability_state || 'Unknown'}</span>
                </button>
              ))}
            </section>
          )}
          {noFixedSpot.length > 0 && (
            <section className="mb-2 last:mb-0">
              <p className="px-1 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">No fixed spot · {noFixedSpot.length}</p>
              <p className="px-1 pb-1 text-[11px] text-gray-500">Virtual or movable locations. Never suggested for put-away.</p>
              {noFixedSpot.map((location) => (
                <button type="button" key={location.location_code} onClick={() => onSelect(location)} className="block w-full rounded px-1.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-50">
                  <span className="block truncate">{location.location_code}</span>
                  {location.geometry_review_note && <span className="block truncate text-[10px] text-gray-400">{location.geometry_review_note}</span>}
                </button>
              ))}
            </section>
          )}
        </div>
      )}
    </aside>
  );
};

export default UnplacedTray;
